import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Icon, Layout, LogoLoader } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { isPast } from 'core/utils/date'

import { type Carrier, type OpenDockWarehouseDetails } from '../../domain/Shipment'
import { useGetOpenDockCarrierId } from '../hooks/useGetOpenDockCarrierId'
import { useGetOpenDockWarehouseDetails } from '../hooks/useGetOpenDockWarehouseDetails'
import { AlreadyScheduled } from './AlreadyScheduled'
import { Schedule } from './Schedule'
import { type ScheduleFormValues } from './Schedule'
import { ScheduleDateAndTime } from './ScheduleDateAndTime'
import { SendInfo } from './SendInfo'
import { WarehouseInstructions } from './WarehouseInstructions'

const STEPS = {
  INSTRUCTIONS: 'instructions',
  SCHEDULE: 'schedule',
  SCHEDULE_DATE_AND_TIME: 'schedule_date_and_time',
  SEND_INFO: 'send_info',
  ALREADY_SCHEDULED: 'already_scheduled',
}

function StepContent({
  step,
  isSignedUp,
  warehouseDetails,
  shipmentCarrier,
  shipmentUUID,
  stopIndex,
  setStep,
  fields,
  setStaticFields,
  facilityTimezone,
  openDockCarrierId,
  onConfirm,
  onClose,
}: {
  step: string
  isSignedUp: boolean
  warehouseDetails: OpenDockWarehouseDetails
  shipmentCarrier: Carrier
  shipmentUUID: string
  stopIndex: number
  setStep: (step: string) => void
  fields: {
    staticFields: Partial<ScheduleFormValues>
    dynamicFields: Record<string, number | string | boolean | null>
  }
  setStaticFields: (staticFields: Partial<ScheduleFormValues>) => void
  facilityTimezone?: string
  openDockCarrierId: string | undefined
  onConfirm: () => void
  onClose: () => void
}) {
  switch (step) {
    case STEPS.INSTRUCTIONS:
      return (
        <WarehouseInstructions
          isSignedUp={isSignedUp}
          warehouseDetails={warehouseDetails}
          shipmentCarrier={shipmentCarrier}
          setStep={setStep}
        />
      )
    case STEPS.SCHEDULE:
      return (
        <Schedule
          isSignedUp={isSignedUp}
          warehouseDetails={warehouseDetails}
          shipmentCarrier={shipmentCarrier}
          setStep={setStep}
          fields={fields}
          setStaticFields={setStaticFields}
        />
      )
    case STEPS.SCHEDULE_DATE_AND_TIME:
      return (
        <ScheduleDateAndTime
          isSignedUp={isSignedUp}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          setStep={setStep}
          fields={fields}
          setStaticFields={setStaticFields}
          facilityTimezone={facilityTimezone}
        />
      )
    case STEPS.SEND_INFO:
      return (
        <SendInfo
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          setStep={setStep}
          fields={fields}
          facilityTimezone={facilityTimezone}
          warehouseDetails={warehouseDetails}
          openDockCarrierId={openDockCarrierId}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      )
    case STEPS.ALREADY_SCHEDULED:
      return (
        <AlreadyScheduled
          setStep={setStep}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

type AppointmentFields = {
  staticFields: ScheduleFormValues
  dynamicFields: Record<string, number | string | boolean | null>
}

export function OpenDockAppointmentDrawer({
  isOpen,
  onClose,
  shipmentUUID,
  shipmentCarrier,
  stopIndex,
  facilityTimezone,
  onConfirm,
  stopDate,
}: {
  isOpen: boolean
  onClose: () => void
  shipmentUUID: string
  shipmentCarrier: Carrier
  stopIndex: number
  facilityTimezone?: string
  onConfirm: () => void
  stopDate: string
}) {
  const [step, setStep] = useState<string>(STEPS.INSTRUCTIONS)

  const [fields, setFields] = useState<AppointmentFields>({
    staticFields: {
      loadType: null,
      dock: null,
      appointmentDate: isPast(new Date(stopDate)) ? null : `${stopDate}T12:00:00.000Z`, // Using the same time and timezone as selected in the datepicker
      appointmentTime: null,
    },
    dynamicFields: {},
  })

  function setStaticFields(staticFields: Partial<ScheduleFormValues>) {
    setFields(prev => ({
      ...prev,
      staticFields: {
        ...prev.staticFields,
        ...staticFields,
      },
    }))
  }

  const { openDockCarrierId, fetchOpenDockCarrierId, status } = useGetOpenDockCarrierId(
    shipmentUUID,
    false
  )

  const {
    warehouseDetails,
    fetchOpenDockWarehouseDetails,
    status: warehouseStatus,
  } = useGetOpenDockWarehouseDetails(shipmentUUID, stopIndex, false)

  useEffect(() => {
    if (status === RequestStatus.Initial && isOpen) {
      fetchOpenDockCarrierId(shipmentUUID)
    }
  }, [fetchOpenDockCarrierId, isOpen, shipmentUUID, status])

  useEffect(() => {
    if (warehouseStatus === RequestStatus.Initial && isOpen) {
      fetchOpenDockWarehouseDetails(shipmentUUID, stopIndex)
    }
  }, [fetchOpenDockWarehouseDetails, isOpen, shipmentUUID, stopIndex, warehouseStatus])

  useEffect(() => {
    if (warehouseStatus === RequestStatus.Error) {
      onClose()
      toast.error('Failed to load warehouse details. Please try again later.')
    }
  }, [onClose, warehouseStatus])

  if (warehouseStatus === RequestStatus.Pending) {
    return (
      <Drawer open={isOpen} onClose={onClose}>
        <Drawer.Header>
          <Layout.Group align="center" gap="spacing-1">
            <Icon name="calendar" size="24px" />
            Schedule
          </Layout.Group>
          <Drawer.Close />
        </Drawer.Header>
        <Drawer.Body>
          <Layout.Stack align="center" paddingT="spacing-16">
            <LogoLoader />
          </Layout.Stack>
        </Drawer.Body>
        <Drawer.Actions>
          <Drawer.ActionTertiary disabled>Enter Confirmation Number</Drawer.ActionTertiary>
          <Drawer.ActionPrimary disabled>Start</Drawer.ActionPrimary>
        </Drawer.Actions>
      </Drawer>
    )
  }

  if (warehouseStatus === RequestStatus.Done && warehouseDetails) {
    return (
      <Drawer open={isOpen} onClose={onClose}>
        <Drawer.Header>
          <Layout.Group align="center" gap="spacing-1">
            <Icon name="calendar" size="24px" />
            Schedule
          </Layout.Group>
          <Drawer.Close />
        </Drawer.Header>
        <StepContent
          step={step}
          isSignedUp={Boolean(openDockCarrierId)}
          openDockCarrierId={openDockCarrierId}
          warehouseDetails={warehouseDetails}
          shipmentCarrier={shipmentCarrier}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          setStep={setStep}
          fields={fields}
          setStaticFields={setStaticFields}
          facilityTimezone={facilityTimezone}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      </Drawer>
    )
  }

  return null
}
